import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Card, Table, Button, Descriptions, Space, Tooltip, Tag, Popconfirm, Switch } from "antd";
import axios from "axios";
import { useState } from "react";

import { showErrorNotification } from "../../../../../utils/notification";
import CreateEditProblemModal from "../../Modals/CreateEditProblemModal";
import CreateEditSetModal from "../../Modals/CreateEditSetModal";
import { formatPreviousSlugs } from "../../SlugFormItem";

const ProblemSetDetails = ({
  problemSetData,
  refreshContentTree = () => {},
  ROOT_URL,
  BASE_URL
}) => {
  const PROBLEM_SET_API = `${ROOT_URL}/${BASE_URL}/problem-set`;
  const PROBLEMS_API = `${PROBLEM_SET_API}/problems`;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditSetModalOpen, setIsEditSetModalOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCreateProblem = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      problemSetUniquePublicId: problemSetData.uniquePublicId
    };
    axios
      .post(`${PROBLEMS_API}/create`, payload)
      .then(() => {
        setIsModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateProblem = (values, problem = selectedProblem) => {
    setLoading(true);
    const payload = {
      ...values,
      uniquePublicId: problem.uniquePublicId
    };

    axios
      .post(`${PROBLEMS_API}/update`, payload)
      .then(() => {
        setIsModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteProblem = (problem) => {
    setLoading(true);
    const payload = {
      uniquePublicId: problem.uniquePublicId
    };

    axios
      .post(`${PROBLEMS_API}/delete`, payload)
      .then(() => {
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateSet = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      uniquePublicId: problemSetData.uniquePublicId
    };

    axios
      .post(`${PROBLEM_SET_API}/update`, payload)
      .then(() => {
        setIsEditSetModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBulkPreviewToggle = (isPreviewAvailable) => {
    setLoading(true);
    axios
      .patch(`${PROBLEM_SET_API}/toggle-preview`, {
        problemSetUniquePublicId: problemSetData.uniquePublicId,
        isPreviewAvailable
      })
      .then(() => {
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      title: "Problem Name",
      dataIndex: "title",
      key: "title",
      width: 220
    },
    {
      title: "Platform",
      dataIndex: "sourcePlatform",
      key: "sourcePlatform",
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: "Difficulty",
      dataIndex: "difficulty",
      key: "difficulty",
      render: (level) => {
        let color = "geekblue";
        if (level === "EASY") color = "green";
        if (level === "MEDIUM") color = "gold";
        if (level === "HARD") color = "red";
        return <Tag color={color}>{level}</Tag>;
      }
    },
    {
      title: "Order",
      dataIndex: "problemSetDisplayOrder",
      key: "problemSetDisplayOrder"
    },
    {
      title: "Visibility",
      dataIndex: "isVisible",
      key: "isVisible",
      render: (val) => (val ? "Visible" : "Hidden")
    },
    {
      title: "Preview",
      dataIndex: "isPreviewAvailable",
      key: "isPreviewAvailable",
      render: (val, record) => (
        <Tooltip title="When ON, non-enrolled users can see the title and solve the problem for free.">
          <Switch
            checked={val}
            onChange={(checked) =>
              handleUpdateProblem({ ...record, isPreviewAvailable: checked }, record)
            }
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedProblem(record);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this problem?"
            onConfirm={() => handleDeleteProblem(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card
        title={problemSetData.title}
        extra={
          <Space wrap>
            <Button
              type="primary"
              onClick={() => {
                setSelectedProblem(null);
                setIsModalOpen(true);
              }}
            >
              + Create Problem
            </Button>
            <Button onClick={() => setIsEditSetModalOpen(true)}>Edit Metadata</Button>
            <Popconfirm
              title={`Are you sure you want to ${
                problemSetData.isPreviewAvailable ? "disable" : "enable"
              } preview for all problems?`}
              onConfirm={() => handleBulkPreviewToggle(!problemSetData.isPreviewAvailable)}
            >
              <Button loading={loading}>
                {problemSetData.isPreviewAvailable ? "Disable All Previews" : "Enable All Previews"}
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Title">{problemSetData.title}</Descriptions.Item>
          <Descriptions.Item label="ID">{problemSetData.uniquePublicId}</Descriptions.Item>
          <Descriptions.Item label="Slug">{problemSetData.slug || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Previous Slugs">
            {formatPreviousSlugs(problemSetData)}
          </Descriptions.Item>
          <Descriptions.Item label="Display Order In Module">
            {problemSetData.moduleDisplayOrder}
          </Descriptions.Item>
          <Descriptions.Item label="Visibility">
            {problemSetData.isVisible ? "Visible" : "Hidden"}
          </Descriptions.Item>
          <Descriptions.Item label="Preview Available">
            {problemSetData.isPreviewAvailable ? (
              <Tag color="blue">Yes</Tag>
            ) : (
              <Tag color="default">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Counts">
            Easy: {problemSetData.easyProblemsCount || 0}, Medium:{" "}
            {problemSetData.mediumProblemsCount || 0}, Hard: {problemSetData.hardProblemsCount || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {problemSetData.createdBy || "N/A"}
          </Descriptions.Item>
        </Descriptions>

        <Table
          dataSource={problemSetData.items || []}
          columns={columns}
          pagination={false}
          rowKey="uniquePublicId"
          scroll={{ x: "max-content" }}
        />
      </Card>

      <CreateEditProblemModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedProblem(null);
        }}
        onCreate={selectedProblem ? handleUpdateProblem : handleCreateProblem}
        loading={loading}
        initialValues={selectedProblem}
        isEdit={!!selectedProblem}
      />

      <CreateEditSetModal
        open={isEditSetModalOpen}
        onCancel={() => setIsEditSetModalOpen(false)}
        onUpdate={handleUpdateSet}
        loading={loading}
        entityName="Problem Set"
        initialValues={{
          title: problemSetData.title,
          slug: problemSetData.slug,
          reservedSlugs: problemSetData.reservedSlugs,
          moduleDisplayOrder: problemSetData.moduleDisplayOrder,
          isVisible: problemSetData.isVisible
        }}
      />
    </>
  );
};

export default ProblemSetDetails;
